import "./Forms.scss";

import React, { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";

import { CustomInput } from "../../../../components/CustomInput/CustomInput";
import { CustomModal } from "../../../../components/CustomModal";
import type { IOrganization } from "../../../../contexts/organizations/organizationsContext";
import type { IKey } from "../../../../hooks/reactQueryHooks/useKeys";
import { useCheckAccess } from "../../../../hooks/useCheckAccess";
import { useDisableSubmit } from "../../../../hooks/useDisableSubmit";

interface EditApiKeyFormProps {
  apiKey: IKey;
  isProcessing?: boolean;
  isError: boolean;
  organization?: IOrganization | null;
  handleKeyChange: (data: IKey | null) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

const EditApiKeyForm: React.FC<EditApiKeyFormProps> = ({
  apiKey,
  isProcessing,
  isError,
  organization,
  onSubmit,
  onCancel,
  handleKeyChange,
}) => {
  const { isDisabled, onUpdateValues, setInitialValue } = useDisableSubmit();
  const noSubscriptionError = `Editing API keys is disabled while there is no active subscription for the ${organization?.name} organization.`;
  const { displayError } = useCheckAccess(noSubscriptionError, isError);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onUpdateValues({ name: val });
    handleKeyChange({
      ...apiKey,
      name: val,
    });
  };

  useEffect(() => {
    setInitialValue({ name: apiKey.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const footerButtons = () => {
    return (
      <>
        <Button
          className="btn btn-secondary btn-sm"
          size="sm"
          variant="secondary"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="btn btn-primary"
          disabled={isDisabled || !!displayError || isProcessing}
          type="submit"
          onClick={onSubmit}
        >
          Update
          {isProcessing && (
            <Spinner className="ms-2" size="sm" animation="border" />
          )}
        </Button>
      </>
    );
  };

  return (
    <CustomModal
      show={true}
      handleClose={onCancel}
      title="Edit API key"
      footer={footerButtons()}
      errorMessage={displayError}
    >
      <div>
        <p>
          The CogCache API key will be used to authenticate and authorise all
          the CogCache API requests.
        </p>
        <CustomInput
          label="API key name"
          handleChange={handleChange}
          inputValue={apiKey.name}
          placeholder="Enter the API key name"
          maxLength={150}
        />
      </div>
    </CustomModal>
  );
};

export { EditApiKeyForm };
